import { Button } from "@design";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getMicroAppOptions } from "@/api";
import { EntityDetailPage } from "@/components/build/EntityDetailPage";
import { EditWithAiButton } from "@/features/build/withAi/EditWithAiButton";
import { MicroAppAboutTab } from "./MicroAppAboutTab";
import { MicroAppAccessTab } from "./MicroAppAccessTab";
import { MicroAppOverviewForm } from "./MicroAppOverviewForm";
import { useDeleteMicroApp } from "./useDeleteMicroApp";

/** Build > Micro-apps > detail: the shared EntityDetailPage scaffold (with an "Open" action); Overview (metadata form), About, Access. */
export function MicroAppDetailPage() {
  const { t } = useTranslation(["build", "microApps"]);
  const navigate = useNavigate();
  const { uuid = "" } = useParams();
  const app = useQuery(getMicroAppOptions({ path: { app_uuid: uuid } }));
  const { onDelete, dialog } = useDeleteMicroApp(() => navigate("/build/apps"));
  const data = app.data?.data;

  return (
    <EntityDetailPage
      listPath="/build/apps"
      rootLabel={t("microApps:title")}
      name={data?.name ?? ""}
      isPending={app.isPending}
      isError={app.isError}
      notFoundLabel={t("microApps:detail.notFound")}
      tabs={[
        {
          value: "overview",
          label: t("microApps:tabs.overview"),
          content: data && <MicroAppOverviewForm app={data} />,
        },
        { value: "about", label: t("microApps:tabs.about"), content: data && <MicroAppAboutTab app={data} /> },
        { value: "access", label: t("microApps:tabs.access"), content: data && <MicroAppAccessTab app={data} /> },
      ]}
      canDelete={data?.canManage ?? false}
      onDelete={() => data && onDelete(data)}
      deleteDialog={dialog}
      extraActions={
        <>
          {data?.canEdit && <EditWithAiButton entityType="micro_app" uuid={data.uuid} />}
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<ExternalLink size={14} />}
            onClick={() => navigate(`/build/apps/${uuid}/view`)}
          >
            {t("microApps:detail.open")}
          </Button>
        </>
      }
    />
  );
}
